import React, { useMemo, useState } from "react";
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  ModalDialog,
  ModalOverflow,
  Stack,
  Typography,
} from "@mui/joy";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function parseDate(value) {
  if (!value) return null;
  const parts = String(value).split("-");
  if (parts.length === 3) {
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

export function toInputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatRangeLabel(value) {
  if (!value) return "-";
  const date = parseDate(value);
  if (!date) return value;
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isFutureDay(day) {
  return day > startOfDay(new Date());
}

function buildCalendarCells(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

const DatePickerModal = ({ open, value, onSelect, onClose }) => {
  const valueObj = parseDate(value) || null;

  const initialView = valueObj || new Date();
  const [viewYear, setViewYear] = useState(initialView.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialView.getMonth());

  const cells = useMemo(
    () => buildCalendarCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("en", {
        month: "long",
        year: "numeric",
      }).format(new Date(viewYear, viewMonth, 1)),
    [viewYear, viewMonth],
  );

  const prevMonth = () => {
    setViewMonth((m) => (m === 0 ? 11 : m - 1));
    setViewYear((y) => (viewMonth === 0 ? y - 1 : y));
  };

  const nextMonth = () => {
    setViewMonth((m) => (m === 11 ? 0 : m + 1));
    setViewYear((y) => (viewMonth === 11 ? y + 1 : y));
  };

  const handleDayClick = (day) => {
    if (isFutureDay(day)) return;
    onSelect(toInputDate(day));
    onClose();
  };

  const dayVariant = (day) => {
    if (sameDay(day, valueObj)) return "solid";
    return "plain";
  };

  const dayColor = (day) => {
    if (sameDay(day, valueObj)) return "primary";
    return "neutral";
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1300 }}>
      <ModalOverflow>
        <ModalDialog
          variant="outlined"
          sx={{ p: 2, width: 320, borderRadius: "md", boxShadow: "lg" }}
        >
          {/* Month navigation */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <IconButton size="sm" variant="plain" onClick={prevMonth}>
              <ChevronLeft />
            </IconButton>
            <Typography level="title-sm">{monthLabel}</Typography>
            <IconButton size="sm" variant="plain" onClick={nextMonth}>
              <ChevronRight />
            </IconButton>
          </Stack>

          {/* Weekday header */}
          <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
            {WEEKDAYS.map((w) => (
              <Grid xs={12 / 7} key={w}>
                <Typography
                  level="body-xs"
                  textAlign="center"
                  sx={{ color: "text.tertiary" }}
                >
                  {w}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Days */}
          <Grid container spacing={0.5}>
            {cells.map((day, i) =>
              day ? (
                <Grid xs={12 / 7} key={day.getTime()}>
                  <Button
                    size="sm"
                    fullWidth
                    variant={dayVariant(day)}
                    color={dayColor(day)}
                    disabled={isFutureDay(day)}
                    sx={{ borderRadius: "sm", minWidth: 0 }}
                    onClick={() => handleDayClick(day)}
                  >
                    {day.getDate()}
                  </Button>
                </Grid>
              ) : (
                <Grid xs={12 / 7} key={`blank-${i}`} />
              ),
            )}
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Actions */}
          <Stack spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default DatePickerModal;
